import * as dayjs from 'dayjs';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isSameData } from '../dto/data-model-utils.js';
import { EventTicketOrder } from './event-ticket-order.js';

export class OfflineTicketOrderCreatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '04782a96389c4613a2eb8e03d5c2ce0c';

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly order: EventTicketOrder,
  ) {
    super();
  }

  get $typeId(): string {
    return OfflineTicketOrderCreatedEvent.TYPE_ID;
  }

  static create(params: {
    uniqueId: string,
    creatorId: string | null,
    deviceId: string | null,
    creationDate: dayjs.Dayjs,
    source: SyncEventSource,
    order: EventTicketOrder,
  }): OfflineTicketOrderCreatedEvent {
    return new OfflineTicketOrderCreatedEvent(
        params.uniqueId,
        params.creatorId,
        params.deviceId,
        params.creationDate,
        params.source,
        params.order,
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): OfflineTicketOrderCreatedEvent {
    return new OfflineTicketOrderCreatedEvent(
        MapUtils.getString(map, 'uniqueId'),
        MapUtils.getStringOrNull(map, 'creatorId'),
        MapUtils.getStringOrNull(map, 'deviceId'),
        context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
        toEnumFromString(MapUtils.getString(map, 'source'), SyncEventSource),
        context.fromData<EventTicketOrder>(MapUtils.get<SerializedData>(map, 'order')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      deviceId: this.deviceId,
      creationDate: context.encodeDate(this.creationDate),
      source: fromEnumAsString(this.source),
      order: context.toData(this.order),
      orderId: this.order.uniqueId,
    };
  }

  clone(): this {
    return new OfflineTicketOrderCreatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        this.creationDate,
        this.source,
        this.order,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.deviceId === this.deviceId &&
      other.creationDate === this.creationDate &&
      other.source === this.source &&
      isSameData(other.order, this.order);
  }

  static verifyType(model: DataModel): boolean {
    return model instanceof OfflineTicketOrderCreatedEvent;
  }

  static assertType(model: DataModel): model is OfflineTicketOrderCreatedEvent {
    return model instanceof OfflineTicketOrderCreatedEvent;
  }
}

export class OfflineTicketOrderCreatedEventBuilder extends DataModelBuilder<OfflineTicketOrderCreatedEvent> {
  build(context: SerializationContext, map: SerializedData): OfflineTicketOrderCreatedEvent {
    return OfflineTicketOrderCreatedEvent.fromMap(context, map);
  }
}
